import React from 'react';
import { observer } from 'mobx-react';
import { Trans } from '@gamiphy/react-i18n';
import { Tier, TierConfig, TierConfigType } from '@gamiphy/bl/level';
import { usePointsLabel } from '@gamiphy/loyalty/points/hooks';
import { round } from '@gamiphy/utils/number';

type Props = {
  nextTier?: Tier,
  tiers?: Tier[],
  accumulated?: number,
  tierConfig?: TierConfig
}

const ProgressDescription: React.FC<Props> = observer(({
  nextTier, accumulated, tiers, tierConfig,
}) => {
  const neededAmount = React.useMemo(
    () => (round(((nextTier?.neededAmount ?? 0) - (accumulated ?? 0)))),
    [nextTier?.neededAmount, accumulated],
  );
  const pointsLabel = usePointsLabel(tierConfig?.type ?? TierConfigType.exp);

  const progressText = React.useMemo(() => {
    switch (true) {
      case !!nextTier:
        if (tierConfig?.type === TierConfigType.valueOfPurchases) {
          return (
            <Trans
              i18nKey="tier.nextLevel.purchasesValue"
              defaults="You need to accumulate {{neededAmount, currency}} in purchases to reach the next level"
              values={{
                neededAmount,
              }}
            />
          );
        }
        return (
          <Trans
            i18nKey="tier.nextLevel"
            defaults="{{neededAmount}} {{label}} to reach your next level"
            values={{
              neededAmount,
              label: pointsLabel,
            }}
          />
        );
      case tiers?.length === 1:
        return (
          <Trans
            i18nKey="tier.exp"
            defaults="You have {{exp}} {{label}}"
            values={{
              exp: accumulated,
              label: pointsLabel,
            }}
          />
        );
      case !nextTier:
        return (
          <Trans
            i18nKey="tier.completed"
            defaults="Congratulations you have reached the final level!"
          />
        );
      default:
        return null;
    }
  }, [nextTier, tiers, accumulated, tierConfig?.type, pointsLabel, neededAmount]);

  return (progressText);
});

export default ProgressDescription;
